import { BodyLink } from '@jouzen/ecom-components/v3/BodyLink';
import { Button } from '@jouzen/ecom-components/v3/Button';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { usePathname } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import type { ReactNode } from 'react';

import { EventType } from '@/analytics/types';
import RichText from '@/app/components/RichText';
import { STORE_POP_PATHNAME } from '@/app/consts/pathnames';
import getLocalePartialUrl from '@/utils/getLocalePartialUrl';
import slugifyString from '@/utils/slugifyString';

import Feature from './Feature';

interface WhoGIftsForProps {
  readonly position: 'top' | 'bottom';
}

interface TValueProps {
  readonly chunks: ReactNode;
  readonly localePartialUrl: string;
}

const ShopButton = () => {
  const path = usePathname();
  const t = useTranslations();

  const handleClick = async () => {
    void window.ouraAnalytics.track(EventType.CTAClicked, {
      cta: slugifyString(t('header_shop_now')),
      location: 'perfect_gift_module',
      module: 'perfect_gift_module',
      path,
    });
  };

  return (
    <Button
      onClick={handleClick}
      href={STORE_POP_PATHNAME}
    >
      {t('header_shop_now')}
    </Button>
  );
};

const trendslink = ({ chunks, localePartialUrl }: TValueProps) => (
  <BodyLink
    href={`https://ouraring.com/blog${localePartialUrl}trends/`}
    target="_blank"
    rel="noopener noreferrer"
  >
    {chunks}
  </BodyLink>
);

const goalslink = ({ chunks, localePartialUrl }: TValueProps) => (
  <BodyLink
    href={`https://ouraring.com/blog${localePartialUrl}new-personalized-activity-features/`}
    target="_blank"
    rel="noopener noreferrer"
  >
    {chunks}
  </BodyLink>
);

const periodlink = ({ chunks, localePartialUrl }: TValueProps) => (
  <BodyLink
    href={`https://ouraring.com/blog${localePartialUrl}period-prediction/`}
    target="_blank"
    rel="noopener noreferrer"
  >
    {chunks}
  </BodyLink>
);

const sleeplink = ({ chunks, localePartialUrl }: TValueProps) => (
  <BodyLink
    href={`https://ouraring.com/blog${localePartialUrl}sleep-new-parents/`}
    target="_blank"
    rel="noopener noreferrer"
  >
    {chunks}
  </BodyLink>
);

const pdplink = (chunks: ReactNode) => (
  <BodyLink href="/product/horizon-silver">{chunks}</BodyLink>
);

const activitylink = ({ chunks, localePartialUrl }: TValueProps) => (
  <BodyLink
    href={`https://ouraring.com/blog${localePartialUrl}automatic-activity-detection/`}
    target="_blank"
    rel="noopener noreferrer"
  >
    {chunks}
  </BodyLink>
);

const travellink = ({ chunks, localePartialUrl }: TValueProps) => (
  <BodyLink
    href={`https://ouraring.com/blog${localePartialUrl}manage-jet-lag/`}
    target="_blank"
    rel="noopener noreferrer"
  >
    {chunks}
  </BodyLink>
);

const bedtimelink = ({ chunks, localePartialUrl }: TValueProps) => (
  <BodyLink
    href={`https://ouraring.com/blog${localePartialUrl}ideal-bedtime/`}
    target="_blank"
    rel="noopener noreferrer"
  >
    {chunks}
  </BodyLink>
);

const naplink = (chunks: ReactNode) => (
  <BodyLink
    href="https://support.ouraring.com/hc/articles/1500009653181"
    target="_blank"
    rel="noopener noreferrer"
  >
    {chunks}
  </BodyLink>
);

const readinesslink = ({ chunks, localePartialUrl }: TValueProps) => (
  <BodyLink
    href={`https://ouraring.com/blog${localePartialUrl}readiness-score/`}
    target="_blank"
    rel="noopener noreferrer"
  >
    {chunks}
  </BodyLink>
);

const WhoGiftsFor = ({ position = 'top' }: WhoGIftsForProps) => {
  const t = useTranslations();
  const locale = useLocale();
  const localePartialUrl = getLocalePartialUrl(locale);

  return (
    <section className="py-20">
      {position === 'top' && (
        <>
          <Feature
            title="starting_out_title"
            body={
              <RichText
                messageKey="starting_out_body"
                tValues={{
                  trendslink: (chunks: ReactNode) =>
                    trendslink({ chunks, localePartialUrl }),
                  goalslink: (chunks: ReactNode) =>
                    goalslink({ chunks, localePartialUrl }),
                }}
              />
            }
            reverse
            image={{
              src: t('gifting-starting-out-activity-image'),
              localizedAlt: 'starting_out_title',
              width: 600,
              height: 693,
            }}
          />
          <Feature
            title="powerful_women_title"
            body={
              <RichText
                messageKey="powerful_women_body"
                tValues={{
                  periodlink: (chunks: ReactNode) =>
                    periodlink({ chunks, localePartialUrl }),
                }}
              />
            }
            image={{
              src: 'gift/powerful-women',
              width: 600,
              height: 693,
              localizedAlt: 'powerful_women_title',
            }}
          />
          <Feature
            title="sleep_deprived_parent_title"
            body={
              <RichText
                messageKey="sleep_deprived_parent_body"
                tValues={{
                  sleeplink: (chunks: ReactNode) =>
                    sleeplink({ chunks, localePartialUrl }),
                }}
              />
            }
            reverse
            image={{
              src: 'gift/sleepy-parent',
              width: 600,
              height: 693,
              localizedAlt: 'sleep_deprived_parent_title',
            }}
            overlayImage={{
              src: t('gifting-sleep-trends-mock-image'),
              fill: true,
              localizedAlt: 'sleep_deprived_parent_title',
              format: 'png',
            }}
          />
          <Feature
            title="fashionista_title"
            body={
              <RichText messageKey="fashionista_body" tValues={{ pdplink }} />
            }
            image={{
              src: 'gift/fashionista',
              width: 600,
              height: 693,
              localizedAlt: 'fashionista_title',
            }}
          />
        </>
      )}
      {position === 'bottom' && (
        <>
          <Feature
            title="fitness_fanatics_title"
            body={
              <RichText
                messageKey="fitness_fanatics_body"
                tValues={{
                  activitylink: (chunks: ReactNode) =>
                    activitylink({ chunks, localePartialUrl }),
                  goalslink: (chunks: ReactNode) =>
                    goalslink({ chunks, localePartialUrl }),
                }}
              />
            }
            image={{
              src: 'gift/fitness-fanatics',
              localizedAlt: 'fitness_fanatics_title',
              width: 600,
              height: 693,
            }}
            overlayImage={{
              src: t('gifting-fitness-fanatics-image'),
              localizedAlt: 'fitness_fanatics_title',
              fill: true,
              format: 'png',
            }}
            reverse
          />
          <Feature
            title="frequent_travelers_title"
            body={
              <RichText
                messageKey="frequent_travelers_body"
                tValues={{
                  travellink: (chunks: ReactNode) =>
                    travellink({ chunks, localePartialUrl }),
                }}
              />
            }
            image={{
              src: t('gifting-frequent-travelers-image'),
              localizedAlt: 'frequent_travelers_title',
              width: 600,
              height: 693,
            }}
          />
          <Feature
            title="nightowls_earlybirds_title"
            body={
              <RichText
                messageKey="nightowls_earlybirds_body"
                tValues={{
                  bedtimelink: (chunks: ReactNode) =>
                    bedtimelink({ chunks, localePartialUrl }),
                }}
              />
            }
            image={{
              src: t('gifting-nightowls-earlybirds-image'),
              localizedAlt: 'nightowls_earlybirds_title',
              width: 600,
              height: 693,
            }}
            reverse
          />
          <Feature
            title="naptakers_title"
            body={
              <RichText
                messageKey="naptakers_body"
                tValues={{
                  naplink,
                  readinesslink: (chunks: ReactNode) =>
                    readinesslink({ chunks, localePartialUrl }),
                }}
              />
            }
            image={{
              src: 'gift/nap-takers',
              width: 600,
              height: 693,
              localizedAlt: 'naptakers_title',
            }}
            overlayImage={{
              src: t('gifting-nap-taker-image'),
              fill: true,
              format: 'png',
              localizedAlt: 'naptakers_title',
            }}
          />
          <Feature
            title="anyone_everyone_title"
            body={<Typography>{t('anyone_everyone_body')}</Typography>}
            button={<ShopButton />}
            image={{
              src: 'gift/anyone-everyone',
              width: 600,
              height: 693,
              localizedAlt: 'anyone_everyone_title',
            }}
            reverse
          />
        </>
      )}
    </section>
  );
};

export default WhoGiftsFor;
